import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { supabase } from '@/lib/supabase';
import { DatabaseAppVersionConfig } from '@/types/database';
import { isVersionLower } from '@/utils/versionComparator';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('AppVersionService');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);

const CACHE_KEY = '@app_version_check_cache';
const CACHE_TIMESTAMP_KEY = '@app_version_check_timestamp';
const RECOMMENDED_DISMISSED_KEY = '@recommended_update_dismissed';
const FORCE_UPDATE_STATE_KEY = '@force_update_state';
const CACHE_VALIDITY_MS = 30 * 60 * 1000; // 30 minutes
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const RECOMMENDED_DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface UpdateCheckResult {
  forceUpdateRequired: boolean;
  recommendedUpdateAvailable: boolean;
  storeUrl: string;
  currentVersion: string;
  minimumVersion: string;
  recommendedVersion: string | null;
}

interface CachedVersionConfig {
  config: DatabaseAppVersionConfig;
  timestamp: number;
}

interface RecommendedDismissed {
  version: string;
  timestamp: number;
}

interface ForceUpdateState {
  forceUpdateRequired: boolean;
  minimumVersion: string;
  timestamp: number;
}

class AppVersionService {
  private cachedConfig: CachedVersionConfig | null = null;
  private inFlightConfigPromise: Promise<DatabaseAppVersionConfig | null> | null = null;

  async getVersionConfig(bypassCache = false): Promise<DatabaseAppVersionConfig | null> {
    const startTime = Date.now();
    logWithTimestamp('🔍 [APP-VERSION] ========================================');
    logWithTimestamp('🔍 [APP-VERSION] Fetching version configuration...');

    if (!bypassCache && this.cachedConfig) {
      const age = Date.now() - this.cachedConfig.timestamp;
      if (age < CACHE_VALIDITY_MS) {
        const duration = Date.now() - startTime;
        logWithTimestamp(`🔍 [APP-VERSION] ✅ Using cached config (age: ${Math.round(age / 1000)}s)`);
        logWithTimestamp(`🔍 [APP-VERSION] ======================================== (${duration}ms)`);
        return this.cachedConfig.config;
      }
      logWithTimestamp('🔍 [APP-VERSION] Cache expired, fetching fresh data...');
    }

    if (this.inFlightConfigPromise && !bypassCache) {
      logWithTimestamp('🔍 [APP-VERSION] Request already in-flight, waiting for existing promise...');
      return this.inFlightConfigPromise;
    }

    this.inFlightConfigPromise = this.fetchVersionConfigFromDatabase();

    try {
      const result = await this.inFlightConfigPromise;
      return result;
    } finally {
      this.inFlightConfigPromise = null;
    }
  }

  private async fetchVersionConfigFromDatabase(): Promise<DatabaseAppVersionConfig | null> {
    const startTime = Date.now();

    try {
      const platformFilter = Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : null;

      if (!platformFilter) {
        logWithTimestamp('🔍 [APP-VERSION] ⚠️ Unsupported platform, skipping version check');
        return null;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        logWithTimestamp('⏱️ [APP-VERSION] Query timeout after 5000ms, aborting...');
        controller.abort();
      }, 5000);

      const { data, error } = await supabase
        .from('app_version_config')
        .select('*')
        .in('platform', [platformFilter, 'both'])
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) {
        const errorDetails = {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        };
        errorWithTimestamp('🔍 [APP-VERSION] Error fetching version config:', JSON.stringify(errorDetails));
        return null;
      }

      if (!data || data.length === 0) {
        logWithTimestamp('🔍 [APP-VERSION] No version configuration found');
        return null;
      }

      // Prioritize platform-specific config over 'both'
      const config = data.find(c => c.platform === platformFilter) || data[0];

      // Cache the config
      this.cachedConfig = {
        config: config as DatabaseAppVersionConfig,
        timestamp: Date.now(),
      };

      // Also store in AsyncStorage for persistence
      try {
        await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(this.cachedConfig));
      } catch (storageError) {
        errorWithTimestamp('🔍 [APP-VERSION] Error storing cache:', storageError);
      }

      const duration = Date.now() - startTime;
      logWithTimestamp(`🔍 [APP-VERSION] ✅ Fetched config for platform: ${config.platform}`);
      logWithTimestamp(`🔍 [APP-VERSION] ======================================== (${duration}ms)`);

      return config as DatabaseAppVersionConfig;
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
        logWithTimestamp('⏱️ [APP-VERSION] Query aborted due to timeout');
      } else {
        errorWithTimestamp('🔍 [APP-VERSION] Unexpected error:', err);
      }

      // Try to use cached data from AsyncStorage as fallback
      try {
        const stored = await AsyncStorage.getItem(CACHE_KEY);
        if (stored) {
          const cached: CachedVersionConfig = JSON.parse(stored);
          logWithTimestamp('🔍 [APP-VERSION] Using fallback cache from AsyncStorage');
          this.cachedConfig = cached;
          return cached.config;
        }
      } catch (storageError) {
        errorWithTimestamp('🔍 [APP-VERSION] Error reading fallback cache:', storageError);
      }

      return null;
    }
  }

  getCurrentAppVersion(): string {
    return Constants.expoConfig?.version || '1.0.0';
  }

  async checkForUpdates(bypassCache = false, isStartupCheck = false, ignoreDismiss = false): Promise<UpdateCheckResult | null> {
    const startTime = Date.now();
    logWithTimestamp('🔄 [APP-VERSION] ========================================');
    logWithTimestamp(`🔄 [APP-VERSION] Checking for app updates... (startup: ${isStartupCheck}, bypassCache: ${bypassCache}, ignoreDismiss: ${ignoreDismiss})`);

    const currentVersion = this.getCurrentAppVersion();
    logWithTimestamp(`🔄 [APP-VERSION] Current app version: ${currentVersion}`);

    // If there's a persisted force update state, return it immediately on startup
    // This ensures the modal shows up even if network fails or we're within interval
    if (isStartupCheck) {
      try {
        const storedState = await AsyncStorage.getItem(FORCE_UPDATE_STATE_KEY);
        if (storedState) {
          const state: ForceUpdateState = JSON.parse(storedState);
          // Verify the force update is still valid for current version
          if (state.forceUpdateRequired && isVersionLower(currentVersion, state.minimumVersion)) {
            logWithTimestamp(`🔄 [APP-VERSION] ⚠️ Using persisted force update state (version ${state.minimumVersion} required)`);
            // Still perform a background check but return the cached state immediately
            this.performBackgroundCheck(bypassCache);

            return {
              forceUpdateRequired: true,
              recommendedUpdateAvailable: false,
              storeUrl: Platform.OS === 'ios'
                ? 'https://apps.apple.com/app/hekaya/id6752662021'
                : 'https://play.google.com/store/apps/details?id=com.virgoplay.hekaya',
              currentVersion,
              minimumVersion: state.minimumVersion,
              recommendedVersion: null,
            };
          } else {
            // Clear invalid state
            await AsyncStorage.removeItem(FORCE_UPDATE_STATE_KEY);
          }
        }
      } catch (err) {
        errorWithTimestamp('🔄 [APP-VERSION] Error reading persisted force update state:', err);
      }
    }

    // Check if we should skip this check (minimum interval)
    // BUT never skip if this is a startup check - we always want to verify on startup
    if (!bypassCache && !isStartupCheck) {
      try {
        const lastCheckStr = await AsyncStorage.getItem(CACHE_TIMESTAMP_KEY);
        if (lastCheckStr) {
          const lastCheck = parseInt(lastCheckStr, 10);
          const timeSinceLastCheck = Date.now() - lastCheck;

          if (timeSinceLastCheck < MIN_CHECK_INTERVAL_MS) {
            const waitTime = Math.round((MIN_CHECK_INTERVAL_MS - timeSinceLastCheck) / 1000);
            logWithTimestamp(`🔄 [APP-VERSION] Skipping check (last check ${Math.round(timeSinceLastCheck / 1000)}s ago, wait ${waitTime}s)`);
            return null;
          }
        }
      } catch (err) {
        errorWithTimestamp('🔄 [APP-VERSION] Error checking last check timestamp:', err);
      }
    }

    const config = await this.getVersionConfig(bypassCache);

    if (!config) {
      const duration = Date.now() - startTime;
      logWithTimestamp('🔄 [APP-VERSION] ⚠️ No configuration available, skipping update check');
      logWithTimestamp(`🔄 [APP-VERSION] ======================================== (${duration}ms)`);
      return null;
    }

    // Store timestamp of this check
    try {
      await AsyncStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Error storing check timestamp:', err);
    }

    // Check force update
    const forceUpdateRequired =
      config.force_update_enabled &&
      isVersionLower(currentVersion, config.minimum_required_version);

    // Check recommended update
    let recommendedUpdateAvailable = false;
    if (
      config.recommended_update_enabled &&
      config.recommended_version &&
      isVersionLower(currentVersion, config.recommended_version)
    ) {
      if (ignoreDismiss) {
        logWithTimestamp('🔄 [APP-VERSION] Ignoring dismiss check (ignoreDismiss=true)');
        recommendedUpdateAvailable = true;
      } else {
        const dismissed = await this.isRecommendedUpdateDismissed(config.recommended_version);
        recommendedUpdateAvailable = !dismissed;
      }
    }

    // Determine store URL based on platform
    const storeUrl =
      Platform.OS === 'ios'
        ? config.ios_store_url || ''
        : Platform.OS === 'android'
          ? config.android_store_url || ''
          : '';

    const result: UpdateCheckResult = {
      forceUpdateRequired,
      recommendedUpdateAvailable,
      storeUrl,
      currentVersion,
      minimumVersion: config.minimum_required_version,
      recommendedVersion: config.recommended_version,
    };

    // Persist force update state if required
    try {
      if (forceUpdateRequired) {
        const state: ForceUpdateState = {
          forceUpdateRequired: true,
          minimumVersion: config.minimum_required_version,
          timestamp: Date.now(),
        };
        await AsyncStorage.setItem(FORCE_UPDATE_STATE_KEY, JSON.stringify(state));
        logWithTimestamp('🔄 [APP-VERSION] ✅ Persisted force update state');
      } else {
        // Clear force update state if no longer required
        await AsyncStorage.removeItem(FORCE_UPDATE_STATE_KEY);
        logWithTimestamp('🔄 [APP-VERSION] ✅ Cleared force update state (no longer required)');
      }
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Error persisting force update state:', err);
    }

    const duration = Date.now() - startTime;
    logWithTimestamp('🔄 [APP-VERSION] Update check results:');
    logWithTimestamp(`🔄 [APP-VERSION]   - Current version: ${currentVersion}`);
    logWithTimestamp(`🔄 [APP-VERSION]   - Minimum required: ${config.minimum_required_version}`);
    logWithTimestamp(`🔄 [APP-VERSION]   - Recommended: ${config.recommended_version || 'N/A'}`);
    logWithTimestamp(`🔄 [APP-VERSION]   - Force update required: ${forceUpdateRequired}`);
    logWithTimestamp(`🔄 [APP-VERSION]   - Recommended update available: ${recommendedUpdateAvailable}`);
    logWithTimestamp(`🔄 [APP-VERSION] ======================================== (${duration}ms)`);

    return result;
  }

  private async performBackgroundCheck(bypassCache = false): Promise<void> {
    logWithTimestamp('🔄 [APP-VERSION] Starting background check to refresh config...');
    try {
      // Perform the check in background without returning result
      await this.checkForUpdates(bypassCache, false);
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Background check failed:', err);
    }
  }

  async isRecommendedUpdateDismissed(version: string): Promise<boolean> {
    try {
      const stored = await AsyncStorage.getItem(RECOMMENDED_DISMISSED_KEY);
      if (!stored) {
        return false;
      }

      const dismissed: RecommendedDismissed = JSON.parse(stored);

      // Check if it's the same version and within 24 hours
      if (dismissed.version === version) {
        const age = Date.now() - dismissed.timestamp;
        if (age < RECOMMENDED_DISMISS_DURATION_MS) {
          logWithTimestamp(`🔄 [APP-VERSION] Recommended update dismissed for version ${version} (${Math.round(age / 1000 / 60)} minutes ago)`);
          return true;
        }
      }

      return false;
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Error checking dismissed status:', err);
      return false;
    }
  }

  async dismissRecommendedUpdate(version: string): Promise<void> {
    try {
      const dismissed: RecommendedDismissed = {
        version,
        timestamp: Date.now(),
      };

      await AsyncStorage.setItem(RECOMMENDED_DISMISSED_KEY, JSON.stringify(dismissed));
      logWithTimestamp(`🔄 [APP-VERSION] Dismissed recommended update for version ${version}`);
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Error storing dismissed status:', err);
    }
  }

  async clearCache(): Promise<void> {
    try {
      await AsyncStorage.multiRemove([
        CACHE_KEY,
        CACHE_TIMESTAMP_KEY,
        RECOMMENDED_DISMISSED_KEY,
      ]);
      this.cachedConfig = null;
      logWithTimestamp('🔄 [APP-VERSION] Cache cleared');
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Error clearing cache:', err);
    }
  }

  async getCacheStats(): Promise<{
    hasCachedConfig: boolean;
    cacheAge: number | null;
    lastCheckAge: number | null;
  }> {
    try {
      let cacheAge: number | null = null;
      let lastCheckAge: number | null = null;

      const stored = await AsyncStorage.getItem(CACHE_KEY);
      if (stored) {
        const cached: CachedVersionConfig = JSON.parse(stored);
        cacheAge = Date.now() - cached.timestamp;
      }

      const lastCheckStr = await AsyncStorage.getItem(CACHE_TIMESTAMP_KEY);
      if (lastCheckStr) {
        const lastCheck = parseInt(lastCheckStr, 10);
        lastCheckAge = Date.now() - lastCheck;
      }

      return {
        hasCachedConfig: !!stored,
        cacheAge: cacheAge ? Math.round(cacheAge / 1000) : null,
        lastCheckAge: lastCheckAge ? Math.round(lastCheckAge / 1000) : null,
      };
    } catch (err) {
      errorWithTimestamp('🔄 [APP-VERSION] Error getting cache stats:', err);
      return {
        hasCachedConfig: false,
        cacheAge: null,
        lastCheckAge: null,
      };
    }
  }
}

export const appVersionService = new AppVersionService();
